// src/pages/ResetPassword.tsx
import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { FiLock } from "react-icons/fi";
import Swal from "sweetalert2";
import { API_BASE_URL } from "../../config";

const ResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const token = searchParams.get("token");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordRequirements, setPasswordRequirements] = useState({
    length: false,
    number: false,
    specialChar: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!token) {
      Swal.fire({
        icon: "error",
        title: "Invalid Link",
        text: "Reset token is missing or invalid.",
        confirmButtonColor: "#6366f1"
      }).then(() => {
        navigate("/forgot-password");
      });
    }
  }, [token]);

  const validatePassword = (password: string) => {
    setPasswordRequirements({
      length: password.length >= 8,
      number: /\d/.test(password),
      specialChar: /[!@#$%^&*(),.?":{}|<>]/.test(password),
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!token) return;

    if (!passwordRequirements.length || !passwordRequirements.number || !passwordRequirements.specialChar) {
      Swal.fire({
        icon: "error",
        title: "Weak Password",
        text: "Password must meet all requirements.",
        confirmButtonColor: "#6366f1"
      });
      return;
    }

    if (newPassword !== confirmPassword) {
      Swal.fire({
        icon: "error",
        title: "Mismatch",
        text: "Passwords do not match.",
        confirmButtonColor: "#6366f1"
      });
      return;
    }

    try {
      setIsSubmitting(true);

      const response = await fetch(`${API_BASE_URL}/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, newPassword }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result?.error || "Reset failed");
      }

      Swal.fire({
        icon: "success",
        title: "Success",
        text: "Your password has been reset. Please log in.",
        confirmButtonColor: "#6366f1"
      }).then(() => {
        navigate("/login");
      });
    } catch (err: any) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: err.message || "Something went wrong",
        confirmButtonColor: "#6366f1"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-blue-50 flex items-center justify-center px-4">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-6">
        <h2 className="text-2xl font-bold text-center text-indigo-700 mb-6">Reset Your Password</h2>
        <form className="space-y-4" onSubmit={handleSubmit}>
          <div>
            <label className="block text-sm font-medium mb-1">New Password</label>
            <div className="relative">
              <FiLock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="password"
                className="pl-10 w-full p-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
                value={newPassword}
                onChange={(e) => {
                  setNewPassword(e.target.value);
                  validatePassword(e.target.value);
                }}
                required
              />
            </div>
            <div className="text-xs mt-2">
              <p className={passwordRequirements.length ? "text-green-600" : "text-gray-500"}>• At least 8 characters</p>
              <p className={passwordRequirements.number ? "text-green-600" : "text-gray-500"}>• Contains a number</p>
              <p className={passwordRequirements.specialChar ? "text-green-600" : "text-gray-500"}>• Contains a special character</p>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Confirm Password</label>
            <div className="relative">
              <FiLock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="password"
                className="pl-10 w-full p-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className={`w-full py-3 px-4 text-white rounded-lg font-medium ${
              isSubmitting ? "bg-indigo-400" : "bg-indigo-600 hover:bg-indigo-700"
            } transition-colors`}
          >
            {isSubmitting ? "Resetting..." : "Reset Password"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ResetPassword;
